import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Form } from 'react-final-form';
import { useNavigate } from 'react-router-dom';
import { has_permission, getIsSuper, getIsPartner } from 'config/permissions';
import Loader from 'components/Loader';
import { showToast } from 'components/Toast';
import { DetailHeader } from 'components/Forms/DetailHeader';
import { TabAccordion } from 'components/Forms/TabAccordion';
import { getUrlParams, wait } from 'helpers/utils';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import {
  getInitialSetValues,
  normalizeSetPages,
  validateCommunityPageForm,
} from 'views/Pages/utils';
import { getCommunityPagesForCommunity } from 'selectors/pages';
import { useAdminAuth } from 'hooks/useAdminAuth';
import { getRecipients } from 'store/recipients/actions';
import { getAllPagesForCommunity, uploadFile } from 'store/pages/actions';
import { checksTab, pageTypes } from 'views/Pages/constants';
import { useAccordionTab } from 'helpers/useAccordionTab';
import { CommunityPageAccordion } from 'views/Pages/Accordions/CommunityPageAccordion';
import { SetsData } from 'store/sets/types';
import {
  useGetSetsQuery,
  useCreateSetMutation,
  useUpdateSetMutation,
  useUpdateImageFilenameMutation,
} from 'services/setsApi';
import { CommunityContentPreview } from './CommunityContentPreview';
import { DuplicateMediaLoader } from './DuplicateMediaLoader';
import { getPageInteractionType, trackEvent } from 'helpers/mixpanelEvents';

const CommunityPagesDetail = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const hasInitialized = useRef(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [tabStatus, setTabStatus] = useState<string[]>(
    new Array(pageTypes.community.length).fill('')
  );
  const [initialFormValues, setInitialFormValues] = useState({});
  const [isPageDuplicated, setIsPageDuplicated] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [editSetPageSelected, setEditSetPageSelected] = useState<SetsData | undefined>(undefined);
  const [isUploadingAssets, setIsUploadingAssets] = useState(false);

  const { clientId, activeRole, hasClient, isSuper } = useAdminAuth();
  const { allPagesByType, allPagesLoading } = useAppSelector(getCommunityPagesForCommunity);

  // Super's "ALL" (0) lists every client's sets (client_id omitted); others are client-scoped.
  const { data: setsFromApi = [], isLoading: setsLoading } = useGetSetsQuery(
    { client_id: clientId ?? 0 },
    { skip: !hasClient && !isSuper }
  );
  const [createSet] = useCreateSetMutation();
  const [updateSet] = useUpdateSetMutation();
  const [updateImageFilename] = useUpdateImageFilenameMutation();

  useEffect(() => {
    setTabStatus(prevStatus => ['in-progress', ...prevStatus.slice(1)]);
    // Used to search for Recipients to add to pages
    if (getIsSuper(activeRole) || getIsPartner(activeRole))
      dispatch(getRecipients({ payment_processor: 'all' }));
  }, [activeRole, dispatch]);

  useEffect(() => {
    // Fire for a specific client or a super's "ALL" (0); the action omits client_id for it.
    if (!hasClient && !isSuper) return;
    dispatch(getAllPagesForCommunity(clientId, activeRole));
  }, [hasClient, isSuper, clientId, activeRole, dispatch]);

  const handleSubmitCommunityPage = async (values: any) => {
    if (!activeRole) return;
    // A set belongs to one client: editing keeps the set's own owner, create/duplicate uses the selection. 0 (super's ALL) and null are not valid targets.
    const targetClientId =
      editSetPageSelected && !isPageDuplicated
        ? (editSetPageSelected.client_id ?? clientId)
        : clientId;
    if (!targetClientId) {
      showToast('Select a specific client before saving a community page.', 'error');
      return;
    }
    setTabStatus(new Array(pageTypes.community.length).fill('success'));
    const setPagesNormalized = normalizeSetPages(values.set_pages);
    const setPagesList = Object.keys(setPagesNormalized);
    if (setPagesList.length === 0) return;

    const body = {
      set_title: values.set_title ?? '',
      set_desc: values.set_desc ?? '',
      querystring: (values.querystring ?? '').toString().toLowerCase().replace(/\s/g, ''),
      display_fundraising_bar: !!values.display_fundraising_bar,
      is_enabled: values.is_enabled,
      start_date: values.startDate,
      end_date: values.endDate,
      set_misc: {
        hide_recent_location: !!values.hide_recent_location,
        goal_alt: values.goal_alt ?? '',
        donate_custom_1: values.donate_custom_1 ?? '',
        donate_custom_1_helper: values.donate_custom_1_helper ?? '',
        show_header_button: values.show_header_button ?? true,
        header_bg_color: values.header_bg_color ?? '',
        primary_color: values.primary_color ?? '',
        secondary_color: values.secondary_color ?? '',
        tertiary_color: values.tertiary_color ?? '',
        issue_misc: {
          card_raised_alt: values.card_raised_alt ?? '',
          card_goal_alt: values.card_goal_alt ?? '',
          card_helper_text: values.card_helper_text ?? '',
          edge_color: values.edge_color ?? '',
          cta_color: values.cta_color ?? '',
        },
        has_header_video:
          values.has_header_video === false
            ? false
            : !!values.setVideoFile ||
              (!isPageDuplicated && !!editSetPageSelected?.set_misc?.has_header_video),
        has_header_logo:
          values.has_header_logo === false
            ? false
            : !!values.setLogoFile ||
              (!isPageDuplicated && !!editSetPageSelected?.set_misc?.has_header_logo),
        sections: (values.sections ?? []).map((section: any, index: number) => {
          const attachedImage = section.sectionFile || section.image_file;
          const hasPreview = !!(attachedImage || section.previewSection);
          const previousHasImage = !!editSetPageSelected?.set_misc?.sections?.[index]?.has_image;

          return {
            id: section.id,
            title: section.title ?? '',
            description: section.description ?? '',
            button_copy: section.button_copy ?? '',
            show_button: section.show_button ?? true,
            cta_button_color: section.cta_button_color ?? null,
            redirect_link: section.redirect_link ?? '',
            layout: section.layout ?? 'NO_IMAGE',
            background_color: section.background_color ?? null,
            text_color: section.text_color ?? null,
            has_image: hasPreview || (section.has_image !== false && previousHasImage),
          };
        }),
        whitelabeled: values.whitelabeled === 'TRUE',
        share_alt: values.share_alt ?? '',
        share_disclaimer: values.share_disclaimer ?? '',
        share_title: values.share_title ?? '',
        share_description: values.share_description ?? '',
        has_share: values.has_share ?? false,
      },
      set_pages: setPagesList,
      set_type: 'COMMUNITY',
      client_id: targetClientId,
    };

    let result;
    if (editSetPageSelected && !isPageDuplicated) {
      const setId = getUrlParams().setId || '';
      if (setId) {
        result = await updateSet({ set_id: setId, body });
      }
    } else {
      result = await createSet({ body });
    }

    if (result && !('error' in result)) {
      const savedSetId: string = (result as any).data?.set_id ?? editSetPageSelected?.set_id ?? '';
      trackEvent('Page Saved', {
        screen: 'page_detail',
        page_category: 'community',
        interaction_type: getPageInteractionType(Boolean(editSetPageSelected), isPageDuplicated),
        page_id: String(savedSetId),
      });
      const savedSetSections =
        ((result as any)?.data?.set_misc?.sections as
          | Array<{ id?: string | number }>
          | undefined) ?? [];

      setIsUploadingAssets(true);
      let anyFailed = false;
      try {
        if (values.setVideoFile) {
          const uuid = crypto.randomUUID();
          const filename = `header_${uuid}.mp4`;
          const uploaded = await (dispatch(
            uploadFile(new File([values.setVideoFile], `sets/${filename}`, { type: 'video' }))
          ) as unknown as Promise<boolean>);
          if (uploaded) {
            await updateImageFilename({
              page_id_value: savedSetId,
              image_type: 'header_video',
              filename,
            }).unwrap();
          } else {
            anyFailed = true;
          }
        }

        if (values.setLogoFile) {
          const uuid = crypto.randomUUID();
          const filename = `set-logo_${uuid}.png`;
          const uploaded = await (dispatch(
            uploadFile(new File([values.setLogoFile], `sets/${filename}`, { type: 'image' }))
          ) as unknown as Promise<boolean>);
          if (uploaded) {
            await updateImageFilename({
              page_id_value: savedSetId,
              image_type: 'header_logo',
              filename,
            }).unwrap();
          } else {
            anyFailed = true;
          }
        }

        for (const [index, section] of (values.sections ?? []).entries()) {
          const attachedImage = section.sectionFile || section.image_file;
          if (attachedImage) {
            const sectionId = savedSetSections?.[index]?.id ?? section.id ?? index;
            const uuid = crypto.randomUUID();
            const filename = `section-${sectionId}_${uuid}.png`;
            const uploaded = await (dispatch(
              uploadFile(new File([attachedImage], `sets/${filename}`, { type: 'image' }))
            ) as unknown as Promise<boolean>);
            if (uploaded) {
              await updateImageFilename({
                page_id_value: savedSetId,
                image_type: `section:${sectionId}`,
                filename,
              }).unwrap();
            } else {
              anyFailed = true;
            }
          }
        }
      } catch {
        anyFailed = true;
      } finally {
        setIsUploadingAssets(false);
      }

      if (!anyFailed) {
        showToast(
          `Success! Your page has been ${editSetPageSelected && !isPageDuplicated ? 'updated' : 'created'}`,
          'success'
        );
        await wait(2000);
        navigate('/community_pages');
      }
    }
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    const setId = getUrlParams().setId;
    if (!setId) {
      hasInitialized.current = true;
      setPageLoaded(true);
      return;
    }
    if (!setsLoading) {
      hasInitialized.current = true;
      setPageLoaded(true);
      if (getUrlParams().copy != null) setIsPageDuplicated(true);
    }
  }, [setsLoading]);

  useEffect(() => {
    if (!pageLoaded || !hasInitialized.current || !Array.isArray(setsFromApi)) return;
    const setId = getUrlParams().setId;
    if (!setId) return;

    const selected = setsFromApi.filter(set => String(set.set_id) === String(setId));
    if (selected?.length > 0) {
      const selectedSet = selected[0];
      setEditSetPageSelected(selectedSet);
      setInitialFormValues(getInitialSetValues(selectedSet, isPageDuplicated));
    }
  }, [setsFromApi, pageLoaded, isPageDuplicated]);

  useEffect(() => {
    const setId = getUrlParams().setId;
    const isNewPage = !setId;
    if (isNewPage && pageLoaded && hasInitialized.current) {
      setInitialFormValues(getInitialSetValues());
    }
  }, [pageLoaded]);

  const updateStatus = (item: number, status: string) => {
    setTabStatus(prevStatus =>
      prevStatus.map((s: string, index: number) => (index === item ? status : s))
    );
  };

  const { handleClickAccordion, tabIndexVisible } = useAccordionTab({
    updateStatus,
    tabStatus,
    pageType: 'community',
    checksTab,
  });

  const formKey = editSetPageSelected ? `set-${editSetPageSelected.set_id}` : 'set-new';

  const setId = getUrlParams().setId;
  const isResolvingSet = setId != null && setId !== '' && !editSetPageSelected;
  const showLoader = setsLoading || isResolvingSet;

  return (
    <>
      {(showLoader || isUploadingAssets) && <Loader type="dots" />}
      <Form
        key={formKey}
        onSubmit={handleSubmitCommunityPage}
        initialValues={initialFormValues}
        validate={validateCommunityPageForm}
        render={({ handleSubmit, form }) => (
          <>
            {!has_permission('COMMUNITY_PAGES_DETAIL_ROUTE', 'R', activeRole) ? (
              <div className="content">
                <h1 className="title-permission">
                  Sorry, you don&apos;t have permission to see this screen.
                </h1>
              </div>
            ) : (
              <>
                {isPageDuplicated && (
                  <DuplicateMediaLoader
                    isPageDuplicated={isPageDuplicated}
                    editSetPageSelected={editSetPageSelected}
                    form={form}
                  />
                )}
                <DetailHeader
                  saveForm={handleSubmit}
                  type="Page"
                  isNew={!editSetPageSelected || isPageDuplicated}
                  backTo="/community_pages"
                  setIsPreviewVisible={setIsPreviewVisible}
                  isPreviewVisible={isPreviewVisible}
                />
                <div className={classNames('content-full-width', { row: isPreviewVisible })}>
                  <div
                    className={classNames({
                      'show-preview': isPreviewVisible,
                    })}
                  >
                    <form onSubmit={handleSubmit}>
                      {pageTypes.community.map((type, index) => (
                        <div key={index}>
                          <TabAccordion
                            index={index}
                            itemVisible={tabIndexVisible}
                            type={type}
                            tabStatus={tabStatus}
                            handleClickAccordion={() => handleClickAccordion(index, form, 'tab')}
                          />

                          <CommunityPageAccordion
                            index={index}
                            itemVisible={tabIndexVisible}
                            formState={form}
                            handleClickAccordion={index =>
                              handleClickAccordion(index, form, 'next')
                            }
                            isPreviewVisible={isPreviewVisible}
                            editSetPageSelected={editSetPageSelected}
                            isPageDuplicated={isPageDuplicated}
                            allPagesByType={allPagesByType}
                            allPagesLoading={allPagesLoading}
                            setsData={setsFromApi}
                            setsLoading={setsLoading}
                          />
                        </div>
                      ))}
                    </form>
                  </div>
                  <CommunityContentPreview isPreviewVisible={isPreviewVisible} />
                </div>
              </>
            )}
          </>
        )}
      />
    </>
  );
};

export default CommunityPagesDetail;
